
import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ModelChartProps {
  data: Array<{ name: string; revenue: number }>;
  loading: boolean;
  dateRangeText: string;
  targetRevenue?: number;
}

const ModelRevenueChart: React.FC<ModelChartProps> = ({ 
  data, 
  loading, 
  dateRangeText,
  targetRevenue = 55000
}) => {
  // Функция для определения цвета столбца на основе выручки
  const getBarColor = (revenue: number) => {
    const percentage = (revenue / targetRevenue) * 100;
    if (percentage < 20) return '#ef4444'; // red-500
    if (percentage < 50) return '#f97316'; // orange-500
    if (percentage < 80) return '#eab308'; // yellow-500
    if (percentage < 100) return '#22c55e'; // green-500
    return '#059669'; // emerald-600
  };

  // Добавляем цвет к данным
  const enhancedData = data.map(item => ({
    ...item,
    fill: getBarColor(item.revenue)
  }));

  // Кастомный тултип с информацией об окупаемости
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const percentage = ((value / targetRevenue) * 100).toFixed(1);
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          <p className="text-blue-600">
            Выручка: {value.toLocaleString()} ₽
          </p>
          <p className="text-muted-foreground text-sm">
            Средняя окупаемость: {percentage}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Выручка по моделям велосипедов за {dateRangeText}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Цветовая схема показывает среднюю окупаемость модели относительно цели {targetRevenue.toLocaleString()} ₽
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center h-80">
            <p>Загрузка данных...</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={enhancedData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 60,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <ReferenceLine 
                y={targetRevenue} 
                stroke="#dc2626" 
                strokeDasharray="5 5" 
                label="Цель окупаемости"
              />
              <Bar dataKey="revenue" name="Выручка" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default ModelRevenueChart;
